import { api } from './api';
import { Report, ReportType } from '../types';

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

export interface ReportGenerationRequest {
  dateRange?: DateRange;
}

const isDevelopmentMode = process.env.NODE_ENV === 'development';

export class ReportService {
  /**
   * Generate a player progress report
   */
  async generatePlayerReport(playerId: string, options?: ReportGenerationRequest): Promise<Report> {
    try {
      const response = await api.post(`/reports/player/${playerId}`, {
        dateRange: options?.dateRange
      });
      return response.data.data;
    } catch (error: any) {
      const isConnectionError = isDevelopmentMode && (
        error.code === 'ERR_NETWORK' || 
        error.code === 'ERR_CONNECTION_REFUSED' ||
        error.code === 'ECONNREFUSED' ||
        error.code === 'ECONNABORTED' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        !error.response
      );
      
      if (isConnectionError) {
        console.warn('🔧 Backend not available, using mock report generation for development');
        
        // Create mock report
        const mockReport: Report = {
          id: `mock-report-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: ReportType.PLAYER,
          title: `Player Development Report`,
          generatedBy: 'mock-user-id',
          generatedFor: playerId,
          data: {
            playerId,
            dateRange: options?.dateRange,
            generatedAt: new Date().toISOString(),
            summary: 'Mock report generated for development purposes'
          },
          fileUrl: `mock://reports/player-${playerId}-${Date.now()}.pdf`,
          expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
          createdAt: new Date()
        };
        
        return mockReport;
      }
      throw error;
    }
  }

  /**
   * Generate a team development report
   */
  async generateTeamReport(teamId: string, options?: ReportGenerationRequest): Promise<Report> {
    const response = await api.post(`/reports/team/${teamId}`, {
      dateRange: options?.dateRange
    });
    return response.data.data;
  }

  /**
   * Generate a club analytics report
   */
  async generateClubReport(clubId: string, options?: ReportGenerationRequest): Promise<Report> {
    const response = await api.post(`/reports/club/${clubId}`, {
      dateRange: options?.dateRange
    });
    return response.data.data;
  }

  /**
   * Get report details
   */
  async getReport(reportId: string): Promise<Report> {
    const response = await api.get(`/reports/${reportId}`);
    return response.data.data;
  }

  /**
   * Get download URL for a report
   */
  async getReportDownloadUrl(reportId: string): Promise<string> {
    try {
      console.log('🔧 Calling API for download URL, reportId:', reportId);
      const response = await api.get(`/reports/${reportId}/download`);
      console.log('🔧 API response:', response.data);
      return response.data.data.downloadUrl;
    } catch (error: any) {
      const isConnectionError = isDevelopmentMode && (
        error.code === 'ERR_NETWORK' || 
        error.code === 'ERR_CONNECTION_REFUSED' ||
        error.code === 'ECONNREFUSED' ||
        error.code === 'ECONNABORTED' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        !error.response
      );
      
      if (isConnectionError) {
        console.warn('🔧 Backend not available, using mock download URL for development');
        return `mock://reports/${reportId}.pdf`;
      }
      throw error;
    }
  }

  /**
   * Get all reports generated by the current user
   */
  async getUserReports(): Promise<Report[]> {
    const response = await api.get('/reports/user/my-reports');
    return response.data.data;
  }

  /**
   * Get all reports for a specific player
   */
  async getPlayerReports(playerId: string): Promise<Report[]> {
    try {
      const response = await api.get(`/reports/player/${playerId}/reports`);
      return response.data.data;
    } catch (error: any) {
      const isConnectionError = isDevelopmentMode && (
        error.code === 'ERR_NETWORK' || 
        error.code === 'ERR_CONNECTION_REFUSED' ||
        error.code === 'ECONNREFUSED' ||
        error.code === 'ECONNABORTED' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        !error.response
      );
      
      if (isConnectionError) {
        console.warn('🔧 Backend not available, using mock player reports for development');
        
        // Return empty array for development - reports will be created when generated
        return [];
      }
      throw error;
    }
  }

  /**
   * Get all reports for a specific team
   */
  async getTeamReports(teamId: string): Promise<Report[]> {
    const response = await api.get(`/reports/team/${teamId}/reports`);
    return response.data.data;
  }

  /**
   * Download a report file
   */
  async downloadReport(reportId: string): Promise<void> {
    console.log('🔧 downloadReport called with reportId:', reportId);
    
    // Always check for mock reports first, regardless of environment
    // This ensures development mode works even if environment detection fails
    if (reportId.startsWith('mock-report-')) {
      console.warn('🔧 Detected mock report, generating PDF download for development');
      
      // Create a comprehensive mock PDF with actual content
      const mockPdfContent = this.generateEnhancedMockPDF(reportId);

      // Create blob and download
      const blob = new Blob([mockPdfContent], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `mock-report-${reportId}.pdf`;
      link.style.display = 'none'; // Hide the link
      
      console.log('🔧 Triggering download...');
      document.body.appendChild(link);
      
      // Try multiple download approaches for better browser compatibility
      try {
        link.click();
        console.log('🔧 Download triggered via click()');
      } catch (clickError) {
        console.warn('🔧 Click method failed, trying dispatchEvent:', clickError);
        // Fallback: use dispatchEvent
        const event = new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: true
        });
        link.dispatchEvent(event);
      }
      
      // Clean up after a short delay to ensure download starts
      setTimeout(() => {
        if (document.body.contains(link)) {
          document.body.removeChild(link);
        }
        URL.revokeObjectURL(url);
        console.log('🔧 Download cleanup completed');
      }, 100);
      
      return;
    }

    try {
      const downloadUrl = await this.getReportDownloadUrl(reportId);
      console.log('🔧 Received download URL:', downloadUrl);
      
      // Check if this is a mock URL that browsers can't handle
      if (downloadUrl.startsWith('mock://')) {
        console.warn('🔧 Detected mock URL, falling back to mock PDF download for development');
        throw new Error('Mock URL detected - triggering fallback');
      }
      
      // Additional safety check for any S3 mock URLs
      if (downloadUrl.includes('mock://s3/')) {
        console.warn('🔧 Detected S3 mock URL, falling back to mock PDF download for development');
        throw new Error('S3 Mock URL detected - triggering fallback');
      }
      
      // Create a temporary link to trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `report-${reportId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error: any) {
      const isConnectionError = isDevelopmentMode && (
        error.code === 'ERR_NETWORK' || 
        error.code === 'ERR_CONNECTION_REFUSED' ||
        error.code === 'ECONNREFUSED' ||
        error.code === 'ECONNABORTED' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        error.response?.status === 500 || // Server errors in development
        !error.response
      );
      
      if (isConnectionError) {
        console.warn('🔧 Backend not available or server error, falling back to mock report download for development');
        
        // Create a comprehensive mock PDF with actual content
        const mockPdfContent = this.generateEnhancedMockPDF(reportId);

        // Create blob and download
        const blob = new Blob([mockPdfContent], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = `mock-report-${reportId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up
        URL.revokeObjectURL(url);
        
        return;
      }
      
      console.error('Error downloading report:', error);
      throw error;
    }
  }

  /**
   * Format report type for display
   */
  formatReportType(type: ReportType): string {
    switch (type) {
      case ReportType.PLAYER:
        return 'Player Report';
      case ReportType.TEAM:
        return 'Team Report';
      case ReportType.CLUB:
        return 'Club Report';
      default:
        return 'Report';
    }
  }

  /**
   * Check if report is expired
   */
  isReportExpired(report: Report): boolean {
    return new Date(report.expiresAt) < new Date();
  }

  /**
   * Generate enhanced mock PDF with realistic content
   */
  private generateEnhancedMockPDF(reportId: string): string {
    const currentDate = new Date().toLocaleDateString();

    // Convert HTML to a more comprehensive PDF structure
    // For now, we'll create a simple text-based PDF with the key information
    return `%PDF-1.4
1 0 obj
<<
/Type /Catalog
/Pages 2 0 R
>>
endobj

2 0 obj
<<
/Type /Pages
/Kids [3 0 R]
/Count 1
>>
endobj

3 0 obj
<<
/Type /Page
/Parent 2 0 R
/MediaBox [0 0 612 792]
/Contents 4 0 R
>>
endobj

4 0 obj
<<
/Length 800
>>
stream
BT
/F1 16 Tf
50 750 Td
(PLAYER DEVELOPMENT REPORT) Tj
0 -30 Td
/F1 12 Tf
(Generated: ${currentDate}) Tj
0 -40 Td
(Report ID: ${reportId}) Tj
0 -30 Td
(Player: Test Player) Tj
0 -20 Td
(Position: Forward) Tj
0 -20 Td
(Team: Development Team) Tj
0 -40 Td
/F1 14 Tf
(PLAYER SKILLS ASSESSMENT) Tj
0 -30 Td
/F1 12 Tf
(Technical Skills - Average: 7.2/10) Tj
0 -20 Td
(  Ball Control: 8/10    Passing: 7/10) Tj
0 -15 Td
(  Positioning: 8/10     Shooting: 6/10) Tj
0 -25 Td
(Physical Attributes - Average: 7.8/10) Tj
0 -20 Td
(  Speed: 8/10          Endurance: 8/10) Tj
0 -15 Td
(  Agility: 9/10        Strength: 7/10) Tj
0 -25 Td
(Psychological - Average: 7.5/10) Tj
0 -20 Td
(  Confidence: 9/10     Motivation: 9/10) Tj
0 -15 Td
(  Decision Making: 7/10 Composure: 6/10) Tj
0 -25 Td
(Social Skills - Average: 8.3/10) Tj
0 -20 Td
(  Teamwork: 9/10       Communication: 8/10) Tj
0 -15 Td
(  Leadership: 7/10     Coachability: 9/10) Tj
0 -40 Td
/F1 14 Tf
(PROGRESS SUMMARY) Tj
0 -25 Td
/F1 12 Tf
(Total Assessments: 2) Tj
0 -15 Td
(Average Improvement: +0.8 points) Tj
0 -15 Td
(Strongest Areas: Teamwork, Agility, Motivation) Tj
0 -15 Td
(Development Areas: Shooting, Decision Making) Tj
0 -30 Td
/F1 10 Tf
(This is a development mode report with mock data.) Tj
ET
endstream
endobj

xref
0 5
0000000000 65535 f 
0000000009 00000 n 
0000000058 00000 n 
0000000115 00000 n 
0000000206 00000 n 
trailer
<<
/Size 5
/Root 1 0 R
>>
startxref
1100
%%EOF`;
  }
}

export const reportService = new ReportService();